import styles from './formStatus.module.scss';

/**
 * Status message component that displays form feedback such as success, error, or informational messages.
 *
 * @remarks
 * This component implements WCAG 4.1.3 Status Messages (Level AA) by using `role="status"` with
 * implicit `aria-live="polite"` to ensure screen readers announce status changes. The container
 * remains in the DOM when hidden to allow assistive technologies to monitor content changes.
 *
 * @param props - The component props object
 * @param props.message - Optional - The status message text to display to the user. Automaticlly hides/shows component based on presence of message.
 * @param props.colour - CSS class name for styling (e.g., 'red' for errors, 'green' for success, 'orange' for warnings)
 * @returns The FormStatus component
 *
 * @example
 * ```tsx
 * // Success message
 * <FormStatus message="Form submitted successfully!" colour="green" />
 *
 * // Error message
 * <FormStatus message="Please correct the errors below." colour="red" />
 *
 * // Warning message
 * <FormStatus message="Some optional fields are incomplete." colour="orange" />
 * ```
 */
const FormStatus = ({
    message,
    colour,
}: {
    message?: string;
    colour: string;
}) => {
    return (
        <div
            className={`${styles.container} ${styles[colour]} ${message ? styles.show : styles.hide}`}
            role="status"
            aria-atomic="true"
        >
            <p>{message}</p>
        </div>
    );
};

export default FormStatus;
